/**
 * AtlasTravelerSelectModule — the "choose your TRAVELER" screen, shown AFTER
 * a world pick and BEFORE anything loads. Redesigned 2026-09-12 to the
 * user's mockup: a stage on the left (the full figure on a brass ring, drag
 * to orbit), the working pane on the right (the scanned-body strip, the
 * record, the roster, BEGIN JOURNEY), looping footage behind everything.
 *
 * A traveler is a LOCAL appearance loadout (name, allegiance, scanned body —
 * localStorage roster): the server keeps its P1 invariant of ONE avatar row
 * per account, and BEGIN JOURNEY simply applies the picked traveler through
 * the normal `setAvatar` (server recomputes complexity) before beginning the
 * journey (`atlas:journey-begin` → the intro → `atlas:menu-enter`, the
 * deferred world entry; lib/journey.ts). BACK returns to the main menu
 * with nothing loaded. The VERSONA tab is a declared future feature — it
 * presents, explains itself, and keeps the avatar flow (the user's call).
 *
 * Record card honesty: LEVEL and PLAYTIME are local travel records (sessions
 * begun + minutes in-world, tracked here); HOME is the REAL residency row
 * when one exists, named through the same settlement-name table the maps
 * use. No server-side progression is invented.
 *
 * The footage (`#ts-video`, public/intro/hf_…mp4, 8 s, muted, looped) plays
 * only while the screen is up and never under reduced motion or automation
 * (a 1080p software decode starves a swiftshader probe — the intro film's
 * law); `localStorage atlas:traveler-video` = 'on' | 'off' overrides.
 */

import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { beginJourney } from '../lib/journey.js';
import { buildAvatar, type AvatarProxy } from '../avatar/AvatarFactory.js';
import { canonicalSkeleton, type AvatarAppearance, type BodyTemplate } from '@shared/avatar.js';
import { PLAYER_MODELS, playerStaticAssetId, modelThumbPath } from '../lib/player_models.js';
import { settlementSites } from '../../../shared/settlements.js';
import { civilizationOf } from '../lib/biome_settlements.js';
import { settlementNamesFor } from '../lib/settlement_names.js';
import type { AssetModule } from './AssetModule.js';

interface Traveler {
  id: string;
  name: string;
  civ: 'a' | 'b';
  body: BodyTemplate;
  skin: string;
  primary: string;
  secondary: string;
  /** Scanned GLB prefab key — always one of PLAYER_MODELS (the classic
   *  primitive body is gone, 2026-09-08; an old '' migrates to the first). */
  model?: string;
  sessions: number;
  playtimeS: number;
  createdAt: number;
}

const STORE_KEY = 'atlas:travelers';
const ACTIVE_KEY = 'atlas:traveler-active';
const VIDEO_KEY = 'atlas:traveler-video';

/** Allegiance palettes: Aurelian gold/copper, Borean ice/silver. */
const CIV_TINTS: Record<'a' | 'b', { primary: string; secondary: string }> = {
  a: { primary: '#e9c46a', secondary: '#b0793f' },
  b: { primary: '#6db3f2', secondary: '#c8d4e6' },
};
const CIV_LABEL: Record<'a' | 'b', string> = { a: 'AURELIAN CONCORD', b: 'BOREAN COMPACT' };

/** Deterministic flavor line (name-hashed) — the record reads like a dossier. */
const BIOS: Record<'a' | 'b', string[]> = {
  a: [
    'A sunsmith of the southern terraces, sworn to the Concord. Follows the light wherever it lands.',
    'A caravan-speaker of the Concord roads. Knows every tariff clause by heart.',
    'A terrace-keeper of the Concord, raised under the gold spires. Tends what grows.',
  ],
  b: [
    'A cartographer of the northern reaches, sworn to the Compact. Maps what the aurora hides.',
    'A tide-reader of the Compact coasts. Trusts the frost more than the thaw.',
    'A watch-keeper of the Compact towers. Counts the long nights without complaint.',
  ],
};

/** The stage: the brass of the paintings' palette, the floor grid in dust. */
const STAGE_BRASS = 0xd9a441;
const STAGE_DUST = 0x8c7a66;
/** Turntable: the idle spin (rad per frame) and how long a drag holds it off. */
const AUTO_SPIN = 0.006;
const ORBIT_IDLE_MS = 2500;
const ORBIT_RAD_PER_PX = 0.011;

function hashStr(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return h >>> 0;
}

function levelOf(t: Traveler): number {
  return 1 + Math.floor(t.playtimeS / 7200) + Math.floor(t.sessions / 3);
}

export class AtlasTravelerSelectModule implements IModule {
  readonly name = 'AtlasTravelerSelect';

  private pendingWorldId: bigint | null = null;
  private roster: Traveler[] = [];
  private activeId = '';
  private newCiv: 'a' | 'b' = 'a';
  private playClock: number | null = null;
  // ── Live 3D preview (its own tiny renderer — never touches the game's) ──
  private pvRenderer: THREE.WebGPURenderer | null = null;
  private pvScene: THREE.Scene | null = null;
  private pvCamera: THREE.PerspectiveCamera | null = null;
  private pvProxy: AvatarProxy | null = null;
  private pvStarting = false;
  private pvLoadToken = 0; // stale-guard for async GLB preview swaps
  private pvRetry: number | null = null; // the catalog poll while a key cannot resolve
  /** Probe diag: the scanned model currently standing in the preview + the last load error. */
  private pvModelShown = '';
  private pvLoadError = '';
  /** Raw-parsed bodies for the preview renderer, one per model key. */
  private readonly pvBodies = new Map<string, THREE.Object3D>();
  private assets: AssetModule | null = null;
  // ── Drag to orbit: the figure's yaw, the idle clock, the drag in flight ──
  private pvYaw = 0;
  private pvDrag: { id: number; x: number; yaw: number } | null = null;
  private pvIdleUntil = 0;
  private pvResize: ResizeObserver | null = null;
  // ── The footage ──
  private videoNote = '';
  private videoWanted = false;
  // ── HOME: settlement names by seed (the maps' own table) ──
  private siteNames: { seed: string; names: Map<number, string> } | null = null;

  private app: App | null = null;

  init(_app: App): void {
    this.app = _app;
    this.assets = _app.getModule('AssetModule') as AssetModule | null; // usually null here: the asset module boots with the world (phase 2)
    const screen = document.getElementById('traveler-select');
    if (!screen) return; // harness/embed: no screen — the menu's direct path stays

    this.load();

    // The preview needs the prefab catalog (prefab_model) BEFORE the world
    // modules boot and subscribe it themselves — a scanned body used to show
    // as primitives here because the key never resolved on this screen.
    bus.on('atlas:account-ready', () => {
      // prefab_model resolves the key → asset id; render_asset is the registry the
      // asset module loads from — both are world-module subscriptions otherwise.
      try { atlas.conn?.subscriptionBuilder().subscribe(['SELECT * FROM prefab_model', 'SELECT * FROM render_asset']); } catch { /* pre-connect */ }
    });

    (globalThis as Record<string, unknown>).__traveler = () => ({
      active: this.activeId, model: this.active() ? this.modelOf(this.active()!) : null, shown: this.pvModelShown, loadError: this.pvLoadError,
      models: PLAYER_MODELS.length, classic: !!document.querySelector('[data-ts-model=""]'),
      caption: document.getElementById('ts-render-sub')?.textContent ?? '',
      previewChildren: this.pvProxy ? this.pvProxy.object.children.filter((c) => c.name.startsWith('player-model:')).map((c) => c.name) : [],
      primitivesVisible: this.pvProxy ? (() => { let n = 0; this.pvProxy!.object.traverse((o) => { if ((o as THREE.Mesh).isMesh && o.visible && !o.name.startsWith('player-model') && !(o.parent && o.parent.name.startsWith('player-model'))) n++; }); return n; })() : 0,
      yaw: this.pvYaw, dragging: this.pvDrag !== null, autoSpin: performance.now() >= this.pvIdleUntil,
      frame: this.figureFrame(),
      video: this.videoDiag(),
      home: document.getElementById('ts-home')?.textContent ?? '',
      roster: this.roster.map((r) => ({ id: r.id, name: r.name, civ: r.civ, model: this.modelOf(r) })),
    });
    // Probe lever: force the footage on/off regardless of the automation gate.
    (globalThis as Record<string, unknown>).__travelerVideo = (on: boolean) => { this.videoWanted = on; this.driveVideo(on); return this.videoDiag(); };

    bus.on('atlas:world-picked', ({ worldId }) => {
      this.pendingWorldId = worldId;
      this.ensureSeedTraveler();
      this.render();
      screen.classList.add('show');
      this.driveVideo(this.videoAllowed());
      void this.startPreview();
    });
    bus.on('atlas:account-ready', ({ username }) => {
      const st = document.getElementById('traveler-status');
      if (st) st.replaceChildren('◈ passport linked — ', Object.assign(document.createElement('b'), { textContent: username }));
      this.ensureSeedTraveler();
    });

    document.getElementById('traveler-back')?.addEventListener('click', () => {
      screen.classList.remove('show');
      this.pausePreview();
      this.driveVideo(false);
      bus.emit('atlas:traveler-back', {});
    });
    document.getElementById('traveler-begin')?.addEventListener('click', () => void this.begin(screen));

    // The strip: the twenty scanned humans, each a portrait card. Picking one
    // re-dresses the active traveler and the stage follows.
    const pick = document.getElementById('ts-model-pick');
    if (pick) {
      for (const m of PLAYER_MODELS) {
        const b = document.createElement('button');
        b.dataset.tsModel = m.key;
        b.className = 'ts-preset';
        b.setAttribute('role', 'option');
        b.title = m.label;
        const img = document.createElement('img');
        img.src = modelThumbPath(m.key);
        img.alt = '';
        img.loading = 'lazy';
        img.draggable = false;
        const cap = document.createElement('span');
        cap.className = 'cap';
        cap.textContent = m.label;
        b.append(img, cap);
        pick.appendChild(b);
      }
      pick.addEventListener('click', (e) => {
        const b = (e.target as HTMLElement).closest('button');
        if (!b) return;
        const t = this.active();
        if (!t) return;
        t.model = b.dataset.tsModel ?? '';
        this.save();
        this.render();
      });
      // The wheel walks the strip sideways (it is a row, not a page).
      pick.addEventListener('wheel', (e) => {
        if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
        pick.scrollLeft += e.deltaY;
        e.preventDefault();
      }, { passive: false });
    }
    const count = document.getElementById('ts-model-count');
    if (count) count.textContent = `${PLAYER_MODELS.length} scanned bodies`;

    // Mode pill: AVATAR is the flow; VERSONA presents as the coming feature.
    const avatarBtn = document.getElementById('ts-mode-avatar');
    const versonaBtn = document.getElementById('ts-mode-versona');
    const note = document.getElementById('ts-mode-note');
    const setMode = (versona: boolean): void => {
      versonaBtn?.classList.toggle('active', versona);
      avatarBtn?.classList.toggle('active', !versona);
      versonaBtn?.setAttribute('aria-selected', String(versona));
      avatarBtn?.setAttribute('aria-selected', String(!versona));
      if (note) note.textContent = versona ? 'versona embodiment arrives in a later update — your avatar walks for now' : '';
    };
    versonaBtn?.addEventListener('click', () => setMode(true));
    avatarBtn?.addEventListener('click', () => setMode(false));

    // New-traveler form: a name and an allegiance; the body comes from the strip.
    const form = document.getElementById('ts-new-form');
    document.getElementById('ts-new')?.addEventListener('click', () => {
      form?.classList.toggle('open');
      if (form?.classList.contains('open')) (document.getElementById('ts-new-name') as HTMLInputElement | null)?.focus();
    });
    const civA = document.getElementById('ts-new-civ-a');
    const civB = document.getElementById('ts-new-civ-b');
    civA?.addEventListener('click', () => { this.newCiv = 'a'; civA.classList.add('on'); civB?.classList.remove('on'); });
    civB?.addEventListener('click', () => { this.newCiv = 'b'; civB.classList.add('on'); civA?.classList.remove('on'); });
    const create = (): void => {
      const nameIn = document.getElementById('ts-new-name') as HTMLInputElement | null;
      const name = (nameIn?.value ?? '').trim();
      if (!name) { nameIn?.focus(); return; }
      const t = this.makeTraveler(name, this.newCiv, 'humanoid');
      t.model = this.active() ? this.modelOf(this.active()!) : PLAYER_MODELS[0].key;
      this.roster.push(t);
      this.activeId = t.id;
      this.save();
      if (nameIn) nameIn.value = '';
      form?.classList.remove('open');
      this.render();
    };
    document.getElementById('ts-new-go')?.addEventListener('click', create);
    document.getElementById('ts-new-name')?.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); create(); } });

    this.wireOrbit();

    // Playtime: minutes in-world accrue to the traveler who began the journey.
    bus.on('atlas:world-entered', () => {
      if (this.playClock !== null) return;
      this.playClock = window.setInterval(() => {
        if (document.hidden || atlas.currentWorldId === null) return; // not in a world → no playtime
        const t = this.roster.find((r) => r.id === this.activeId);
        if (t) { t.playtimeS += 60; this.save(); }
      }, 60_000);
    });
  }

  // ── Roster persistence ──────────────────────────────────────────────────────

  private load(): void {
    try {
      this.roster = JSON.parse(localStorage.getItem(STORE_KEY) ?? '[]') as Traveler[];
      this.activeId = localStorage.getItem(ACTIVE_KEY) ?? '';
    } catch { this.roster = []; }
    if (!Array.isArray(this.roster)) this.roster = [];
    // No CLASSIC body since 2026-09-08: a record without a scanned model wears the first.
    for (const r of this.roster) r.model = this.modelOf(r);
  }

  private save(): void {
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(this.roster));
      localStorage.setItem(ACTIVE_KEY, this.activeId);
    } catch { /* storage full/blocked — the roster just won't persist */ }
  }

  private makeTraveler(name: string, civ: 'a' | 'b', body: BodyTemplate): Traveler {
    return {
      id: `t${Date.now().toString(36)}${Math.floor(Math.random() * 1e4)}`,
      name, civ, body,
      skin: '#d9b8a6',
      primary: CIV_TINTS[civ].primary,
      secondary: CIV_TINTS[civ].secondary,
      sessions: 0, playtimeS: 0, createdAt: Date.now(),
    };
  }

  /** First run: seed one traveler from the passport so the screen never opens empty. */
  private ensureSeedTraveler(): void {
    if (this.roster.length > 0) return;
    const username = atlas.conn ? this.usernameOf() : 'traveler';
    const civ: 'a' | 'b' = hashStr(username) % 2 === 0 ? 'a' : 'b';
    const t = this.makeTraveler(username, civ, 'humanoid');
    this.roster.push(t);
    this.activeId = t.id;
    this.save();
  }

  private usernameOf(): string {
    const conn = atlas.conn;
    if (!conn || atlas.identityHex === null) return 'traveler';
    for (const a of conn.db.account.iter()) {
      if (a.identity.toHexString() === atlas.identityHex) return a.username;
    }
    return 'traveler';
  }

  /**
   * The player's real residency, as the record's HOME ('wanderer' without
   * one) — the settlement's NAME through the maps' own table (DB settlementId
   * = site.id + 1), else the raw row while the Commons seed is not in yet.
   */
  private homeOf(): string {
    const conn = atlas.conn;
    if (!conn || atlas.identityHex === null) return 'wanderer';
    for (const r of conn.db.residency.iter()) {
      if (r.identity.toHexString() !== atlas.identityHex) continue;
      return this.settlementName(Number(r.settlementId)) ?? `settlement #${r.settlementId}`;
    }
    return 'wanderer';
  }

  private settlementName(settlementId: number): string | null {
    const conn = atlas.conn;
    if (!conn) return null;
    let seed = '';
    for (const w of conn.db.world.iter()) if (w.kind === 'commons') { seed = w.terrainSeed; break; }
    if (!seed) return null;
    if (!this.siteNames || this.siteNames.seed !== seed) {
      try { this.siteNames = { seed, names: settlementNamesFor(settlementSites(seed), (ss) => civilizationOf(ss)) }; } catch { return null; }
    }
    return this.siteNames.names.get(settlementId - 1) ?? null;
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  private active(): Traveler {
    return this.roster.find((t) => t.id === this.activeId) ?? this.roster[0];
  }

  private render(): void {
    const t = this.active();
    if (!t) return;
    this.activeId = t.id;
    const set = (id: string, text: string): void => {
      const el = document.getElementById(id);
      if (el) el.textContent = text;
    };
    set('ts-name', t.name.toUpperCase());
    const civEl = document.getElementById('ts-civ');
    if (civEl) { civEl.textContent = `● ${CIV_LABEL[t.civ]}`; civEl.className = `ts-civ ${t.civ}`; }
    set('ts-bio', BIOS[t.civ][hashStr(t.name) % BIOS[t.civ].length]);
    set('ts-level', String(levelOf(t)));
    const h = t.playtimeS / 3600;
    set('ts-playtime', h >= 1 ? `${Math.floor(h)}h` : `${Math.floor(t.playtimeS / 60)}m`);
    set('ts-home', this.homeOf());
    // Strip state: the worn body is marked and kept in view.
    const model = this.modelOf(t);
    for (const b of Array.from(document.querySelectorAll<HTMLButtonElement>('[data-ts-model]'))) {
      const on = (b.dataset.tsModel ?? '') === model;
      b.classList.toggle('on', on);
      b.setAttribute('aria-selected', String(on));
      if (on) this.keepInView(b);
    }
    this.rebuildPreview();

    const roster = document.getElementById('ts-roster');
    if (!roster) return;
    roster.replaceChildren(...this.roster.map((r) => {
      const b = document.createElement('button');
      b.className = `ts-roster-item${r.id === t.id ? ' active' : ''}`;
      const thumb = document.createElement('img');
      thumb.className = 'ts-thumb';
      thumb.src = modelThumbPath(this.modelOf(r));
      thumb.alt = '';
      thumb.draggable = false;
      const col = document.createElement('span');
      const n = document.createElement('div');
      n.className = 'n';
      n.textContent = r.name.toUpperCase();
      const m = document.createElement('div');
      m.className = `m ${r.civ}`;
      m.textContent = `${CIV_LABEL[r.civ]} · LV ${levelOf(r)}`;
      col.append(n, m);
      b.append(thumb, col);
      b.addEventListener('click', () => { this.activeId = r.id; this.save(); this.render(); });
      return b;
    }));
  }

  /** Scroll the strip so the marked card sits inside the visible run (no page jump). */
  private keepInView(b: HTMLElement): void {
    const strip = b.parentElement;
    if (!strip) return;
    const left = b.offsetLeft, right = left + b.offsetWidth;
    if (left < strip.scrollLeft) strip.scrollLeft = Math.max(0, left - 8);
    else if (right > strip.scrollLeft + strip.clientWidth) strip.scrollLeft = right - strip.clientWidth + 8;
  }

  // ── The footage ─────────────────────────────────────────────────────────────

  private videoAllowed(): boolean {
    let pref = '';
    try { pref = localStorage.getItem(VIDEO_KEY) ?? ''; } catch { /* blocked storage */ }
    if (pref === 'off') return false;
    if (pref === 'on') return true;
    if (typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    return !navigator.webdriver;
  }

  private driveVideo(play: boolean): void {
    const v = document.getElementById('ts-video') as HTMLVideoElement | null;
    const note = document.getElementById('ts-video-note');
    if (!v) return;
    this.videoWanted = play;
    if (play) {
      v.preload = 'auto';
      v.muted = true;
      this.videoNote = 'background · live footage';
      void v.play().catch((e: Error) => { this.videoNote = `background · still (${e?.name ?? 'refused'})`; if (note) note.textContent = this.videoNote; });
    } else {
      // A still frame is enough here: reduced motion, a probe, or the screen going down.
      if (!v.paused) v.pause();
      if (v.preload === 'none') { v.preload = 'metadata'; try { v.currentTime = 0.1; } catch { /* no metadata yet */ } }
      this.videoNote = 'background · still frame';
    }
    if (note) note.textContent = this.videoNote;
  }

  private videoDiag(): { present: boolean; playing: boolean; currentTime: number; wanted: boolean; note: string; src: string } {
    const v = document.getElementById('ts-video') as HTMLVideoElement | null;
    return {
      present: !!v, playing: !!v && !v.paused && !v.ended && v.readyState > 2, currentTime: v?.currentTime ?? 0,
      wanted: this.videoWanted, note: this.videoNote, src: v?.currentSrc || v?.getAttribute('src') || '',
    };
  }

  // ── The live 3D preview ─────────────────────────────────────────────────────

  private appearanceOf(t: Traveler): AvatarAppearance {
    return {
      bodyTemplate: t.body,
      skeletonId: canonicalSkeleton(t.body),
      materialSlots: {
        skin: { tint: t.skin },
        primary: { tint: t.primary },
        secondary: { tint: t.secondary },
      },
      attachments: {},
    };
  }

  /** Drag on the stage turns the figure; the turntable resumes after a pause. */
  private wireOrbit(): void {
    const stage = document.getElementById('ts-render');
    const canvas = document.getElementById('ts-preview-canvas');
    if (!stage || !canvas) return;
    canvas.addEventListener('pointerdown', (e) => {
      if (e.button !== 0 && e.pointerType === 'mouse') return;
      this.pvDrag = { id: e.pointerId, x: e.clientX, yaw: this.pvYaw };
      this.pvIdleUntil = Number.POSITIVE_INFINITY;
      stage.classList.add('orbiting');
      try { canvas.setPointerCapture(e.pointerId); } catch { /* synthetic pointer */ }
      e.preventDefault();
    });
    canvas.addEventListener('pointermove', (e) => {
      if (!this.pvDrag || e.pointerId !== this.pvDrag.id) return;
      this.pvYaw = this.pvDrag.yaw + (e.clientX - this.pvDrag.x) * ORBIT_RAD_PER_PX;
      if (this.pvProxy) this.pvProxy.object.rotation.y = this.pvYaw;
    });
    const end = (e: PointerEvent): void => {
      if (!this.pvDrag || e.pointerId !== this.pvDrag.id) return;
      this.pvDrag = null;
      this.pvIdleUntil = performance.now() + ORBIT_IDLE_MS;
      stage.classList.remove('orbiting');
      try { canvas.releasePointerCapture(e.pointerId); } catch { /* not captured */ }
    };
    canvas.addEventListener('pointerup', end);
    canvas.addEventListener('pointercancel', end);
  }

  /** Lazily boot the preview renderer (WebGL2 fallback — a menu never needs WebGPU). */
  private async startPreview(): Promise<void> {
    if (this.pvStarting) { this.resumePreview(); return; }
    const canvas = document.getElementById('ts-preview-canvas') as HTMLCanvasElement | null;
    if (!canvas) return;
    this.pvStarting = true;
    try {
      const r = new THREE.WebGPURenderer({ canvas, antialias: true, forceWebGL: true, alpha: true });
      await r.init();
      const w = Math.max(2, canvas.clientWidth), h = Math.max(2, canvas.clientHeight);
      r.setSize(w, h, false);
      r.setPixelRatio(Math.min(2, window.devicePixelRatio));
      // Filmic tone mapping + transparent clear: the scanned PBR bodies get
      // cinematic response and the footage shows through the stage.
      r.toneMapping = THREE.ACESFilmicToneMapping;
      r.toneMappingExposure = 1.12;
      r.setClearColor(0x000000, 0);
      this.pvRenderer = r;
      this.pvScene = new THREE.Scene();
      // Three-point rig in the paintings' light: a warm key, a cool rim, a soft fill over a hemi base.
      this.pvScene.add(new THREE.HemisphereLight(0xe8dcc8, 0x2a2118, 1.5));
      const key = new THREE.DirectionalLight(0xffe9c8, 2.8);
      key.position.set(1.8, 2.6, 2.2);
      this.pvScene.add(key);
      const rim = new THREE.DirectionalLight(0x9fc6e6, 1.6);
      rim.position.set(-2.2, 1.8, -2.4);
      this.pvScene.add(rim);
      const fill = new THREE.DirectionalLight(0xd9c8b0, 0.7);
      fill.position.set(-1.4, 0.8, 2.6);
      this.pvScene.add(fill);
      // The stage: a soft contact shadow, the brass ring the feet stand on, a
      // floor grid falling away in dust (the mockup's stage).
      const sc = document.createElement('canvas');
      sc.width = sc.height = 128;
      const g = sc.getContext('2d')!;
      const grad = g.createRadialGradient(64, 64, 6, 64, 64, 62);
      grad.addColorStop(0, 'rgba(0,0,0,0.55)');
      grad.addColorStop(0.65, 'rgba(0,0,0,0.22)');
      grad.addColorStop(1, 'rgba(0,0,0,0)');
      g.fillStyle = grad;
      g.fillRect(0, 0, 128, 128);
      const shadowTex = new THREE.CanvasTexture(sc);
      const shadow = new THREE.Mesh(
        new THREE.PlaneGeometry(2.3, 2.3),
        new THREE.MeshBasicMaterial({ map: shadowTex, transparent: true, depthWrite: false }),
      );
      shadow.rotation.x = -Math.PI / 2;
      shadow.position.y = 0.005;
      this.pvScene.add(shadow);
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(0.9, 0.935, 96),
        new THREE.MeshBasicMaterial({ color: STAGE_BRASS, transparent: true, opacity: 0.85, side: THREE.DoubleSide, depthWrite: false }),
      );
      ring.rotation.x = -Math.PI / 2;
      ring.position.y = 0.01;
      this.pvScene.add(ring);
      const grid = new THREE.GridHelper(7, 21, STAGE_BRASS, STAGE_DUST);
      const gm = grid.material as THREE.Material;
      gm.transparent = true;
      gm.opacity = 0.16;
      gm.depthWrite = false;
      grid.position.y = 0.0;
      this.pvScene.add(grid);
      // The camera frames the FULL figure — head clear of the lead line, feet
      // on the ring in the lower quarter, seen a little from above so the ring
      // reads as an ellipse (the mockup). The first cut (32° at 3.3 m) put the
      // head under the header.
      this.pvCamera = new THREE.PerspectiveCamera(34, w / h, 0.05, 20);
      this.pvCamera.position.set(0, 1.42, 4.15);
      this.pvCamera.lookAt(0, 0.92, 0);
      // The stage fills the pane and follows a resize (the canvas is absolute).
      this.pvResize = new ResizeObserver(() => this.fitPreview());
      this.pvResize.observe(canvas.parentElement ?? canvas);
      this.rebuildPreview();
      this.resumePreview();
      canvas.dataset.ready = '1';
    } catch {
      this.pvStarting = false; // headless/no-GL — the stage stands alone
    }
  }

  /** Where the standing body lands on the canvas: top/bottom as fractions of its height (0 = top). */
  private figureFrame(): { top: number; bottom: number } | null {
    const cam = this.pvCamera, proxy = this.pvProxy;
    if (!cam || !proxy) return null;
    const body = proxy.object.children.find((c) => c.name.startsWith('player-model:'));
    if (!body) return null;
    const box = new THREE.Box3().setFromObject(body);
    if (box.isEmpty()) return null;
    let top = 1, bottom = 0;
    const v = new THREE.Vector3();
    for (let i = 0; i < 8; i++) {
      v.set(i & 1 ? box.max.x : box.min.x, i & 2 ? box.max.y : box.min.y, i & 4 ? box.max.z : box.min.z).project(cam);
      const y = (1 - v.y) / 2;
      top = Math.min(top, y); bottom = Math.max(bottom, y);
    }
    return { top, bottom };
  }

  private fitPreview(): void {
    const r = this.pvRenderer, cam = this.pvCamera;
    const canvas = document.getElementById('ts-preview-canvas') as HTMLCanvasElement | null;
    if (!r || !cam || !canvas) return;
    const w = Math.max(2, canvas.clientWidth), h = Math.max(2, canvas.clientHeight);
    r.setSize(w, h, false);
    cam.aspect = w / h;
    cam.updateProjectionMatrix();
  }

  /** The traveler's scanned body — always a PLAYER_MODELS key. */
  private modelOf(t: Traveler): string {
    return t.model && PLAYER_MODELS.some((m) => m.key === t.model) ? t.model : PLAYER_MODELS[0].key;
  }

  private labelOf(key: string): string {
    const m = PLAYER_MODELS.find((x) => x.key === key);
    if (!m) return key;
    return m.label.charAt(0) + m.label.slice(1).toLowerCase();
  }

  private rebuildPreview(): void {
    if (!this.pvScene) return;
    const t = this.active();
    if (!t) return;
    if (this.pvProxy) {
      // The cached raw body is ours to keep: lift it off the old stage before that stage is disposed.
      for (const c of [...this.pvProxy.object.children]) if (c.name.startsWith('player-model:')) this.pvProxy.object.remove(c);
      this.pvScene.remove(this.pvProxy.object);
      this.pvProxy.dispose();
    }
    // The proxy is the stage (turntable pivot); its primitive body NEVER shows
    // — the preview is the full scanned model, or nothing while it streams
    // ("when I select an avatar the full model shows, not the classic one").
    this.pvProxy = buildAvatar(this.appearanceOf(t));
    this.pvProxy.object.traverse((o) => { if ((o as THREE.Mesh).isMesh) o.visible = false; });
    this.pvProxy.object.rotation.y = this.pvYaw;
    this.pvScene.add(this.pvProxy.object);
    const key = this.modelOf(t);
    const token = ++this.pvLoadToken;
    this.setPreviewCaption('loading the body…');
    if (this.pvRetry !== null) { window.clearInterval(this.pvRetry); this.pvRetry = null; }
    const attempt = (): boolean => {
      if (token !== this.pvLoadToken) return true; // switched away: stop
      // The asset module is a WORLD module — it boots on the world pick, while
      // this screen is already up: resolve it on every try, never once at init.
      this.assets ??= (this.app?.getModule('AssetModule') as AssetModule | null) ?? null;
      if (!this.assets || !atlas.conn) return false;
      const assetId = playerStaticAssetId(atlas.conn, key); // THE SWITCH (lib/player_models.ts): today's body by default
      if (assetId === null) return false; // the catalog has not landed — try again shortly
      const assets = this.assets;
      // A RAW parse for this screen's OWN renderer: the shared cache's textures
      // are uploaded to the game renderer and their CPU chains stripped (the
      // F15 mip strip), so a second renderer drew the body as an unready
      // magenta shell. The donor loader keeps the chains; one body per key is
      // kept for the session (~20 × a few MB, never disposed).
      const cached = this.pvBodies.get(key);
      const fetch = cached ? Promise.resolve(cached) : assets.loadDonorScene(assetId, 2048).then((raw) => {
        if (!raw) throw new Error(`render_asset #${assetId} could not be parsed`);
        raw.name = `player-model:${key}`;
        this.pvBodies.set(key, raw);
        return raw;
      });
      void fetch.then((body) => {
        if (token !== this.pvLoadToken || !this.pvProxy || !this.pvScene) return; // switched away
        body.parent?.remove(body);
        this.pvProxy.object.add(body);
        this.pvModelShown = key;
        this.pvLoadError = '';
        this.setPreviewCaption(`${this.labelOf(key)} · your avatar, as the world sees it`);
      }).catch((e: Error) => {
        if (token !== this.pvLoadToken) return;
        this.pvLoadError = String(e?.message ?? e);
        // Transient on this screen: the asset module's loader is still coming
        // up with the world modules, or the registry row has not replicated —
        // the poll below asks again; a real failure surfaces once it gives up.
        if (/not initialized|not found in the registry/.test(this.pvLoadError)) { pending = false; return; }
        this.setPreviewCaption('this body could not be loaded — pick another');
      });
      return true;
    };
    let pending = false; // a load is in flight (or succeeded); false = ask again
    const step = (): 'done' | 'again' => {
      if (token !== this.pvLoadToken) return 'done';
      if (pending) return 'again'; // in flight
      if (!attempt()) return 'again';
      pending = true;
      return 'again'; // keep the clock until the load settles or the budget runs out
    };
    step();
    // The prefab catalog + the asset module land after this screen shows: poll
    // until the body is on the stage (≤ 90 s), then say so if it never came.
    let tries = 0;
    this.pvRetry = window.setInterval(() => {
      const r = step();
      const shown = this.pvModelShown === key && token === this.pvLoadToken;
      if (r === 'done' || shown || ++tries > 180) {
        if (this.pvRetry !== null) { window.clearInterval(this.pvRetry); this.pvRetry = null; }
        if (!shown && token === this.pvLoadToken && r !== 'done') this.setPreviewCaption(`this body could not be loaded — ${this.pvLoadError || 'pick another'}`);
      }
    }, 500);
  }

  private setPreviewCaption(text: string): void {
    const cap = document.getElementById('ts-render-sub');
    if (cap && cap.textContent !== text) cap.textContent = text;
  }

  private resumePreview(): void {
    const r = this.pvRenderer;
    if (!r || !this.pvScene || !this.pvCamera) return;
    r.setAnimationLoop(() => {
      // The slow turntable resumes once a drag has rested.
      if (this.pvProxy && !this.pvDrag && performance.now() >= this.pvIdleUntil) {
        this.pvYaw += AUTO_SPIN;
        this.pvProxy.object.rotation.y = this.pvYaw;
      }
      void r.render(this.pvScene!, this.pvCamera!);
    });
  }

  private pausePreview(): void {
    this.pvRenderer?.setAnimationLoop(null);
  }

  // ── Begin the journey ───────────────────────────────────────────────────────

  private async begin(screen: HTMLElement): Promise<void> {
    const t = this.active();
    const btn = document.getElementById('traveler-begin') as HTMLButtonElement | null;
    if (btn) btn.disabled = true;
    if (t && atlas.conn) {
      t.sessions += 1;
      this.save();
      // The traveler IS the avatar: one server row per account (P1 law) —
      // selection here writes it through the normal recompute path. A failure
      // never blocks entry (the previous appearance simply walks).
      try {
        await atlas.conn.reducers.setAvatar({
          bodyTemplate: t.body,
          skeletonId: canonicalSkeleton(t.body),
          materialSlots: JSON.stringify({
            skin: { tint: t.skin },
            primary: { tint: t.primary },
            secondary: { tint: t.secondary },
          }),
          // The scanned-model choice rides the attachments record to every
          // client — this is what lets OTHERS see who you are in the world.
          attachments: JSON.stringify({ model: this.modelOf(t) }),
        });
      } catch (e) {
        // Entry must never block on appearance — but a silent swallow hid a
        // real rejection during verification. Say what happened.
        console.warn('[Traveler] setAvatar failed — entering with the stored look:', (e as Error).message ?? e);
      }
    }
    this.pausePreview();
    this.driveVideo(false);
    screen.classList.remove('show');
    if (btn) btn.disabled = false;
    // The journey begins: the intro gate (the narration card, then the film)
    // forwards atlas:menu-enter when it ends or is skipped — the world's load
    // waits for it (lib/journey.ts; a page without the gate enters at once).
    beginJourney(this.pendingWorldId);
  }

  dispose(): void {
    if (this.playClock !== null) { window.clearInterval(this.playClock); this.playClock = null; }
    this.pvResize?.disconnect();
    this.pvResize = null;
  }
}
